// API Base URL - Use environment variable if available, otherwise default to production
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://realtygenie-backend.onrender.com';

// Authentication endpoints
export const AUTH_ENDPOINTS = {
  ME: `${API_BASE_URL}/api/auth/me`,
  PERMISSIONS: `${API_BASE_URL}/api/auth/permissions`,
  CHECK_PERMISSION: `${API_BASE_URL}/api/auth/check-permission`,
  ROLES_PERMISSIONS: `${API_BASE_URL}/api/auth/roles-permissions`,
  LOGIN: `${API_BASE_URL}/api/auth/UserLogin`, // Actual endpoint used by frontend
  REGISTER: `${API_BASE_URL}/api/auth/register`,
  AGENCY_REGISTER: `${API_BASE_URL}/api/auth/AgencyRegister`,
  // For requesting a reset link (forgot password). The actual reset (token+password) uses /reset-password.
  RESET_PASSWORD_REQUEST: `${API_BASE_URL}/api/auth/request-password-reset`,
  RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
  RESEND_VERIFICATION: `${API_BASE_URL}/api/auth/resend-verification`,
  VERIFY_EMAIL: `${API_BASE_URL}/api/auth/verify-email`,
} as const;

// Document endpoints
export const DOCUMENT_ENDPOINTS = {
  CATEGORIES: `${API_BASE_URL}/api/documents/categories`,
  SUBCATEGORIES: `${API_BASE_URL}/api/documents/subcategories`,
  DOCUMENT_TYPES: `${API_BASE_URL}/api/documents/document_types`,
  DOCUMENT_FIELDS: `${API_BASE_URL}/api/documents/document_fields`,
  FIELD_VALUES_BY_IDS: `${API_BASE_URL}/api/documents/field_values_by_ids_fetch`, // Fetch field values (POST)
  FIELD_VALUES_SAVE: `${API_BASE_URL}/api/documents/field_values_by_ids`, // Save field values (POST)
  COMPANY_FIELD_VALUES_BY_IDS: `${API_BASE_URL}/api/documents/company_field_values_by_ids_fetch`, // Fetch company field values (POST)
  COMPANY_FIELD_VALUES_SAVE: `${API_BASE_URL}/api/documents/company_field_values_by_ids`, // Save company field values (POST)
  DOCUMENTS: `${API_BASE_URL}/api/documents/documents`,
  DOCUMENTS_BY_IDS: `${API_BASE_URL}/api/documents/documents_by_ids`,
  COMPANY_DOCUMENTS_BY_IDS: `${API_BASE_URL}/api/documents/company_documents_by_ids`,
  UPLOAD: `${API_BASE_URL}/api/documents/upload`,
  DELETE: `${API_BASE_URL}/api/documents/delete`,
  DOWNLOAD: (docID: string) => `${API_BASE_URL}/api/documents/download/${docID}`,
  DOCUMENT_MAP: `${API_BASE_URL}/api/documents/document_map`,
  AGENT_MAP: `${API_BASE_URL}/api/documents/agent_map`,
  AGENCY_MAP: `${API_BASE_URL}/api/documents/agency_map`,
  USER_DOCUMENTS: (userId: string) => `${API_BASE_URL}/api/documents/documents/user/${userId}`,
} as const;

// Listings endpoints
export const LISTING_ENDPOINTS = {
  TYPES: `${API_BASE_URL}/api/listings/types`,
  CLIENT_TYPES: `${API_BASE_URL}/api/listings/client-types`,
  DOCUMENT_TYPES: `${API_BASE_URL}/api/listings/document-types`,
  CREATE: `${API_BASE_URL}/api/listings/listing`,
  BY_ID: (id: string) => `${API_BASE_URL}/api/listings/${id}`,
  USER_LISTINGS: (userId: string) => `${API_BASE_URL}/api/listings/user/${userId}`,
  AGENCY_LISTINGS: (agencyId: string) => `${API_BASE_URL}/api/listings/agency/${agencyId}`,
  DOCUMENT_UPLOAD: `${API_BASE_URL}/api/listings/documents/upload`,
  IMAGE_UPLOAD: `${API_BASE_URL}/api/listings/images/upload`,
  DELETE_IMAGE: (imageId: string) => `${API_BASE_URL}/api/listings/images/${imageId}`,
  DELETE_DOCUMENT: (documentId: string) => `${API_BASE_URL}/api/listings/documents/${documentId}`,
} as const;

// Education Hub endpoints
export const EDUCATION_ENDPOINTS = {
  // Guides
  GUIDES: `${API_BASE_URL}/api/education/guides`,
  GUIDES_ADMIN: `${API_BASE_URL}/api/education/guides/admin/all`,
  GUIDES_ITEM: (id: string) => `${API_BASE_URL}/api/education/guides/${id}`,
  GUIDES_CATEGORIES: `${API_BASE_URL}/api/education/guides/meta/categories`,
  GUIDES_CONTENT: (guideId: string) => `${API_BASE_URL}/api/education/guides/${guideId}/content`,
  
  // Guide Completions
  GUIDE_COMPLETIONS: `${API_BASE_URL}/api/education/guide-completions`,
  GUIDE_COMPLETIONS_STATS: `${API_BASE_URL}/api/education/guide-completions/agency`,
  
  // News
  NEWS: `${API_BASE_URL}/api/education/news`,
  NEWS_ADMIN: `${API_BASE_URL}/api/education/news/admin/all`,
  NEWS_ITEM: (id: string) => `${API_BASE_URL}/api/education/news/${id}`,
  NEWS_FEATURED: `${API_BASE_URL}/api/education/news/featured/posts`,
  NEWS_CATEGORIES: `${API_BASE_URL}/api/education/news/meta/categories`,
  
  // Update Notes
  UPDATES: `${API_BASE_URL}/api/education/updates`,
  UPDATES_ADMIN: `${API_BASE_URL}/api/education/updates/admin/all`,
  UPDATES_ITEM: (id: string) => `${API_BASE_URL}/api/education/updates/${id}`,
  UPDATES_LATEST: `${API_BASE_URL}/api/education/updates/latest/note`,
  UPDATES_CATEGORIES: `${API_BASE_URL}/api/education/updates/meta/categories`,
  UPDATES_TYPES: `${API_BASE_URL}/api/education/updates/meta/types`,
  
  // Resources
  RESOURCES: `${API_BASE_URL}/api/education/resources`,
  RESOURCES_ADMIN: `${API_BASE_URL}/api/education/resources/admin/all`,
  RESOURCES_ITEM: (id: string) => `${API_BASE_URL}/api/education/resources/${id}`,
  RESOURCES_UPLOAD: `${API_BASE_URL}/api/education/resources/upload`,
  RESOURCES_CATEGORIES: `${API_BASE_URL}/api/education/resources/meta/categories`,
  RESOURCES_TAGS: `${API_BASE_URL}/api/education/resources/meta/tags`,
  RESOURCES_DOWNLOAD: (resourceId: string) => `${API_BASE_URL}/api/education/resources/download/${resourceId}`,
  
  // Templates
  TEMPLATES: `${API_BASE_URL}/api/templates`,
  TEMPLATES_ADMIN: `${API_BASE_URL}/api/templates/admin/all`,
  TEMPLATES_BY_DOCTYPE: `${API_BASE_URL}/api/templates/by-document-type`,
  TEMPLATES_CATEGORIES: `${API_BASE_URL}/api/templates/meta/categories`,
  TEMPLATES_DOWNLOAD: (templateId: string) => `${API_BASE_URL}/api/templates/download/${templateId}`,
  TEMPLATES_DOCTYPE_DOWNLOAD: (docTypeId: string, selectedUserId: string) => `${API_BASE_URL}/api/templates/doc_type_download/${docTypeId}/${selectedUserId}`,
  
  // Media
  MEDIA: `${API_BASE_URL}/api/education/media`,
  MEDIA_BATCH: `${API_BASE_URL}/api/education/media/batch`,
  MEDIA_INFO: `${API_BASE_URL}/api/education/media/info`,
} as const;

// Contact endpoints
export const CONTACT_ENDPOINTS = {
  SUBMIT: `${API_BASE_URL}/api/contact/submit`,
} as const;

// Legislation endpoints
export const LEGISLATION_ENDPOINTS = {
  LINKS: `${API_BASE_URL}/api/legislation`,
  CATEGORIES: `${API_BASE_URL}/api/legislation/categories`,
  ADMIN_LINKS: `${API_BASE_URL}/api/legislation/admin/links`,
  ADMIN_LINK: (id: number) => `${API_BASE_URL}/api/legislation/admin/links/${id}`,
  ADMIN_CATEGORIES: `${API_BASE_URL}/api/legislation/admin/categories`,
  ADMIN_CATEGORY: (id: string) => `${API_BASE_URL}/api/legislation/admin/categories/${id}`,
  ADMIN_SUBCATEGORIES: `${API_BASE_URL}/api/legislation/admin/subcategories`,
  ADMIN_SUBCATEGORY: (id: string) => `${API_BASE_URL}/api/legislation/admin/subcategories/${id}`,
} as const;

// Subscription / payment endpoints
export const SUBSCRIPTION_ENDPOINTS = {
  STATUS: `${API_BASE_URL}/api/subscription/status`,
  CREATE_PAYMENT: `${API_BASE_URL}/api/subscription/create-payment`,
  UPGRADE: `${API_BASE_URL}/api/subscription/upgrade`,
  RENEW: `${API_BASE_URL}/api/subscription/renew`,
  PRICES: `${API_BASE_URL}/api/subscription/prices`,
  START_TRIAL: `${API_BASE_URL}/api/subscription/start-trial`
} as const;

// Client endpoints
export const CLIENT_ENDPOINTS = {
  CLIENT_TYPES: `${API_BASE_URL}/api/clients/client-types`,
  DOCUMENT_TYPES: `${API_BASE_URL}/api/clients/document-types`,
  DOCUMENT_UPLOAD: `${API_BASE_URL}/api/clients/documents/upload`,
  DELETE_CLIENT_DOCUMENT: (documentId: string) => `${API_BASE_URL}/api/clients/documents/${documentId}`,
  CLIENTS: `${API_BASE_URL}/api/clients`,
  BY_LISTING: (listingId: string) => `${API_BASE_URL}/api/clients/listing/${listingId}`,
  BY_ID: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}`,
} as const;

// Agency endpoints
export const AGENCY_ENDPOINTS = {
  USERS: (agencyId: string) => `${API_BASE_URL}/api/agency/${agencyId}/users`,
} as const;

export const DASHBOARD_ENDPOINTS = {
  RISK_AGENTS: `${API_BASE_URL}/api/dashboard/risk_agents`,
  FFC_STATUS: `${API_BASE_URL}/api/dashboard/company_ffc_status`,
  AGENT_FFC_STATUS: `${API_BASE_URL}/api/dashboard/agent_ffc_status`,
  BEE_STATUS: `${API_BASE_URL}/api/dashboard/company_bee_status`,
} as const;

// Compliance portal endpoints
export const COMPLIANCE_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/api/compliance/login`,
  ME: `${API_BASE_URL}/api/compliance/me`,
  AGENCIES: `${API_BASE_URL}/api/compliance/agencies`,
  AGENCY_LISTINGS: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/listings`,
  AGENCY_DOCUMENT_MAP: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/document-map`,
  AGENCY_DOCUMENT_MAP_EXPORT: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/document-map/export`,
  AGENCY_DOCUMENT_COMPLIANCE_DATA: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/document-compliance-data`,
  AGENCY_FFC_STATUS: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/ffc-status`,
  AGENCY_BEE_STATUS: (agencyId: string) => `${API_BASE_URL}/api/compliance/agencies/${agencyId}/bee-status`,
} as const;

// Storage keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  SELECTED_AGENCY: 'agency',
} as const;
